import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  Globe,
  LayoutDashboard,
  Save,
  RefreshCw,
  Info,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  MousePointerClick,
  Eye,
  TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Ad System Configuration Page
 * Manage Google Ad Manager settings and preview ad placements
 */
const AdSystemConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    enabled: false,
    network_code: '',
    ad_units: {
      homepage_banner: '',
      sidebar_rectangle: '',
      in_feed: '',
      footer_banner: ''
    }
  });
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load config from backend
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const [configRes, placementsRes, statsRes] = await Promise.all([
          fetch(`${API}/api/admin/ads/config`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/api/admin/ads/placements`),
          fetch(`${API}/api/admin/ads/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(data.config);
          setLastUpdated(data.last_updated);
        }
        
        if (placementsRes.ok) {
          const data = await placementsRes.json();
          setPlacements(data.placements || []);
        }
        
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data.stats || []);
        }
      } catch (error) {
        console.error('Failed to load ad config:', error);
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/ads/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
        toast.success('Configuration saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await fetch(`${API}/api/admin/ads/config/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({ ...prev, enabled: data.enabled }));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to toggle Google Ads');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="ad-system-config">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />
          Ad System Configuration
        </h1>
        <p className="text-muted-foreground mt-2">
          Configure Google Ad Manager and manage ad placements
        </p>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground mt-1">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      <Tabs defaultValue="google" className="space-y-6">
        <TabsList>
          <TabsTrigger value="google">
            <Globe className="w-4 h-4 mr-2" />
            Google Ad Manager
          </TabsTrigger>
          <TabsTrigger value="placements">
            <LayoutDashboard className="w-4 h-4 mr-2" />
            Ad Placements
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="help">
            <Info className="w-4 h-4 mr-2" />
            Setup Guide
          </TabsTrigger>
        </TabsList>

        {/* Google Ad Manager Tab */}
        <TabsContent value="google" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Google Ad Manager Settings</span>
                <Badge variant={config.enabled ? "default" : "secondary"}>
                  {config.enabled ? "Enabled" : "Disabled"}
                </Badge>
              </CardTitle>
              <CardDescription>
                Configure your Google Ad Manager credentials to serve Google ads alongside partner ads
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Google Ads</Label>
                  <p className="text-sm text-muted-foreground">
                    When disabled, only partner/sponsor ads will be shown
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={handleToggle}
                  data-testid="gam-toggle"
                />
              </div>

              {/* Network Code */}
              <div className="space-y-2">
                <Label htmlFor="networkCode">Network Code</Label>
                <Input
                  id="networkCode"
                  placeholder="e.g., 12345678"
                  value={config.network_code}
                  onChange={(e) => setConfig({ ...config, network_code: e.target.value })}
                  disabled={!config.enabled}
                  data-testid="network-code-input"
                />
                <p className="text-xs text-muted-foreground">
                  Find this in Google Ad Manager → Admin → Global Settings → Network Code
                </p>
              </div>

              {/* Ad Units */}
              <div className="space-y-4">
                <Label className="text-base">Ad Unit Paths</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Enter the full ad unit path for each placement (e.g., /12345678/homepage_banner)
                </p>
                
                <div className="grid gap-4">
                  {Object.entries(config.ad_units || {}).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={key} className="text-sm capitalize">
                        {key.replace(/_/g, ' ')}
                      </Label>
                      <Input
                        id={key}
                        placeholder={`/${config.network_code || 'XXXXXXXX'}/${key}`}
                        value={value}
                        onChange={(e) => setConfig({
                          ...config,
                          ad_units: { ...config.ad_units, [key]: e.target.value }
                        })}
                        disabled={!config.enabled}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSave} className="w-full" disabled={saving} data-testid="save-config-btn">
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ad Placements Tab */}
        <TabsContent value="placements" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ad Placement Overview</CardTitle>
              <CardDescription>
                Current ad placements across the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {placements.map((placement) => (
                  <div key={placement.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">{placement.name}</h4>
                      <p className="text-sm text-muted-foreground">{placement.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {placement.sizes.map(s => `${s[0]}x${s[1]}`).join(', ')}
                      </Badge>
                      <Badge variant={placement.active ? "default" : "secondary"}>
                        {placement.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ad Source Priority</CardTitle>
              <CardDescription>
                How ads are served when multiple sources are available
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li><strong>Partner/Sponsor Ads</strong> - Internal promotional content with highest priority</li>
                <li><strong>Member Ads</strong> - Pro/Premium member promotional pages</li>
                <li><strong>Google Ad Manager</strong> - Programmatic ads when no internal ads available</li>
              </ol>
              <Alert className="mt-4">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription>
                  Partner ads always take priority over Google ads to maximize revenue from direct partnerships.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Ad Performance
              </CardTitle>
              <CardDescription>
                Track impressions, clicks, and CTR across placements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {stats.map((stat) => (
                    <Card key={stat.placement} className="bg-muted/30">
                      <CardContent className="pt-4">
                        <h4 className="font-medium capitalize mb-3">
                          {stat.placement.replace(/_/g, ' ')}
                        </h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <Eye className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-bold">{stat.impressions.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Impressions</p>
                          </div>
                          <div>
                            <MousePointerClick className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-bold">{stat.clicks.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Clicks</p>
                          </div>
                          <div>
                            <TrendingUp className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-bold">{stat.ctr}%</p>
                            <p className="text-xs text-muted-foreground">CTR</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No ad performance data yet</p>
                  <p className="text-sm">Stats will appear as ads are served</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Setup Guide Tab */}
        <TabsContent value="help" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Google Ad Manager Setup Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Step 1: Create a Google Ad Manager Account</h4>
                <p className="text-sm text-muted-foreground">
                  Sign up at{' '}
                  <a 
                    href="https://admanager.google.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    admanager.google.com
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Step 2: Find Your Network Code</h4>
                <p className="text-sm text-muted-foreground">
                  Go to Admin → Global Settings → Network Code. It's usually an 8-digit number.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Step 3: Create Ad Units</h4>
                <p className="text-sm text-muted-foreground">
                  Create ad units for each placement:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Homepage Banner (970x90, 728x90, 320x50)</li>
                  <li>Sidebar Rectangle (300x250, 300x600)</li>
                  <li>In-Feed (300x250, 336x280)</li>
                  <li>Footer Banner (728x90, 320x50)</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Step 4: Configure Line Items</h4>
                <p className="text-sm text-muted-foreground">
                  Set up line items in GAM to serve ads to your ad units. You can use:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>AdSense backfill for programmatic ads</li>
                  <li>Direct campaigns for sponsorship deals</li>
                  <li>House ads for internal promotions</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">Step 5: Update Configuration</h4>
                <p className="text-sm text-muted-foreground">
                  Enter your Network Code and Ad Unit paths in the Google Ad Manager tab above,
                  then enable Google Ads.
                </p>
              </div>

              <Alert>
                <Info className="w-4 h-4" />
                <AlertDescription>
                  <strong>Note:</strong> For production deployment, ad configuration should be moved to 
                  environment variables or a secure backend configuration system.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>GPT.js Integration Code</CardTitle>
              <CardDescription>
                Reference code for Google Publisher Tags integration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
{`<!-- Google Publisher Tag in index.html -->
<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>
<script>
  window.googletag = window.googletag || {cmd: []};
  googletag.cmd.push(function() {
    // Define ad slots
    googletag.defineSlot('/${config.networkCode || 'YOUR_NETWORK_CODE'}/homepage_banner', 
      [[970, 90], [728, 90], [320, 50]], 'div-gpt-homepage')
      .addService(googletag.pubads());
    
    // Enable services
    googletag.pubads().enableSingleRequest();
    googletag.enableServices();
  });
</script>`}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(`<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>`)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdSystemConfig;
